import React from 'react';
import {Prompt} from 'react-router-dom';

import http from 'api/http';
import { componentAPI, componentFamilyAPI } from 'api/requests';

import Select, {selectType} from 'Components/Select';
import {Input, inputStyleClasses} from 'Components/Input';
import Button, {buttonColor, buttonType} from 'Components/Button';
import Modal from 'Components/Modal';
import FilePicker from 'Components/FilePicker';
import ApplicationActions from 'Components/ApplicationActions';
import ComponentList from './ComponentList';

import NotificationManager from 'dom-managers/notification';
import { stopPropagation } from 'helpers';

export default class BatchFileLoadList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      name: '',
      categories: '',
      selectedCategory: '',
      selectedType: '',
      listRequestInProcess: true,
      files: [],
      additionalFilterFields: [],
      inputErrors: {},
      requestInProcess: false,
      isModalOpen: false,
      familyComponents: [],
      availableComponentsList: [],
    };

    this.requiredFields = ['name', 'selectedCategory', 'selectedType'];

    this.componentsIdsToAdd = [];

    this.initialized = false;
  }

  componentDidMount() {
    if (!this.initialized) this.initializeInfo();
  }

  componentDidUpdate(prevProps) {
    if (!this.initialized) this.initializeInfo();
  }

  initializeInfo() {
    if (this.props.categories && this.props.components && !this.props.componentsRequestInProcess) {
      let filteredCategories = this.props.categories
        .map(category => {
          return {
            name: category.name,
            componentTypes: category.componentTypes
              .map(type => {
                if (type.allowComponentFamily) {
                  return {
                    componentTypeName: type.componentTypeName,
                    id: type.id,
                  };
                } else {
                  return null;
                }
              })
              .filter(type => type),
          };
        })
        .filter(category => !!category.componentTypes.length);
      this.initialized = true;
      this.setState(
        {
          categories: filteredCategories,
          selectedCategory: filteredCategories[0],
          selectedType: filteredCategories[0].componentTypes[0],
        },
        this.getAvailableComponentsList
      );
    }
  }

  handleInputChange = field => (value, error) => {
    const { inputErrors } = this.state;
    this.someDataWasInputted = true;

    if (!error || error.ops) {
      let newInputErrors = inputErrors;
      delete newInputErrors[field];
      this.setState({ [field]: value, inputErrors: newInputErrors });
    }
  };

  handleCategorySelect = selectedCategory => {
    const { inputErrors } = this.state;
    this.someDataWasInputted = true;

    let newInputErrors = inputErrors;
    delete newInputErrors.selectedCategory;

    this.setState({ selectedCategory, selectedType: '', inputErrors: newInputErrors }, () => {
      this.handleTypeSelect(selectedCategory.componentTypes[0]);
    });
  };

  handleTypeSelect = selectedType => {
    const { inputErrors } = this.state;
    this.someDataWasInputted = true;

    let newInputErrors = inputErrors;
    delete newInputErrors.selectedType;

    this.setState({ selectedType, inputErrors: newInputErrors, familyComponents: [] }, this.getAvailableComponentsList);
  };

  handleFileSelect = files => {
    const { inputErrors } = this.state;
    this.someDataWasInputted = true;

    let newInputErrors = inputErrors;
    delete newInputErrors.files;

    this.setState(prevState => ({
      files: [...prevState.files, ...files],
      inputErrors: newInputErrors,
    }));
  };

  handleRemoveFile = index => () => {
    this.someDataWasInputted = true;
    this.setState(prevState => {
      prevState.files.splice(index, 1);
      return { files: prevState.files };
    });
  };

  getAvailableComponentsList = () => {
    const { components } = this.props;
    const { selectedType } = this.state;

    http.get(componentAPI.listWithoutFamily).then(({ data: noFamilyList }) => {
      let availableComponentsList = components.filter(component => component.componentType.id === selectedType.id);

      availableComponentsList.forEach(component => {
        if (!noFamilyList.find(componentWithNoFamily => componentWithNoFamily.id === component.id))
          component.disabled = true;
      });

      this.setState({ availableComponentsList });
    });
  };

  selectComponentForAdd = component => {
    let { availableComponentsList } = this.state;

    if (!component.disabled) {
      component.checked = !component.checked;

      this.setState({ availableComponentsList });
    }
  };

  togleAllComponentsForAdd = togleTo => event => {
    let { availableComponentsList } = this.state;

    stopPropagation(event);

    availableComponentsList.forEach(component => {
      if (!component.disabled) {
        component.checked = togleTo;
      }
      this.setState({ availableComponentsList });
    });
  };

  addComponents = () => {
    let { availableComponentsList, familyComponents } = this.state;
    this.someDataWasInputted = true;

    let componentsToAdd = availableComponentsList.filter(component => component.checked);

    availableComponentsList = availableComponentsList.filter(component => !component.checked);

    componentsToAdd.forEach(component => {
      component.checked = false;
      component.newlyAdded = true;

      this.componentsIdsToAdd.push(component.id);
    });
    familyComponents.push(...componentsToAdd);

    this.setState({ availableComponentsList, familyComponents });
  };

  selectComponentForDeletion = component => event => {
    let { familyComponents } = this.state;

    stopPropagation(event);

    component.checked = !component.checked;

    this.setState({ familyComponents });
  };

  deleteComponents = () => {
    let { availableComponentsList, familyComponents } = this.state;

    this.modalHeader = 'Confirmation';
    this.modalBody = 'Are you sure you want to remove selected components from component family?';
    this.modalConfirmAction = () => {
      this.someDataWasInputted = true;

      let componentsToDelete = familyComponents.filter(component => component.checked);

      familyComponents = familyComponents.filter(component => !component.checked);

      componentsToDelete.forEach(component => {
        component.checked = false;
        delete component.newlyAdded;
        const additionListIndex = this.componentsIdsToAdd.indexOf(component.id);
        this.componentsIdsToAdd.splice(additionListIndex, 1);
      });
      availableComponentsList.push(...componentsToDelete);

      this.setState({ familyComponents, availableComponentsList }, this.closeModal);
    };
    this.openModal();
  };

  deleteAllComponents = () => {
    let { availableComponentsList, familyComponents } = this.state;
    this.modalHeader = 'Confirmation';
    this.modalBody = 'Are you sure you want to remove selected components from component family?';
    this.modalConfirmAction = () => {
      this.someDataWasInputted = true;

      familyComponents.forEach(component => {
        component.checked = false;
        delete component.newlyAdded;
        const additionListIndex = this.componentsIdsToAdd.indexOf(component.id);
        this.componentsIdsToAdd.splice(additionListIndex, 1);
      });
      availableComponentsList.push(...familyComponents);

      this.setState({ familyComponents: [], availableComponentsList }, this.closeModal);
    };
    this.openModal();
  };

  deleteSingleComponent = component => {
    let { availableComponentsList, familyComponents } = this.state;
    this.someDataWasInputted = true;

    const componentIndex = familyComponents.indexOf(component);

    familyComponents.splice(componentIndex, 1);

    component.checked = false;
    delete component.newlyAdded;
    const additionListIndex = this.componentsIdsToAdd.indexOf(component.id);
    this.componentsIdsToAdd.splice(additionListIndex, 1);

    availableComponentsList.push(component);

    this.setState({ familyComponents, availableComponentsList });
  };

  validateForm = () => {
    const { user } = this.props;
    const { inputErrors } = this.state;
    let newInputErrors = inputErrors;

    const isEmptyArray = object => {
      return object.length !== undefined && typeof object === 'object' && !object.length;
    };

    const isEmptyString = object => {
      return !object.length && typeof object === 'string' && !object.length;
    };

    const isEmptyObject = object => {
      return object.length === undefined && typeof object === 'object' && !Object.keys(object).length;
    };

    this.requiredFields.forEach(fieldName => {
      if (
        isEmptyString(this.state[fieldName]) ||
        isEmptyArray(this.state[fieldName]) ||
        isEmptyObject(this.state[fieldName])
      )
        newInputErrors[fieldName] = true;
    });

    return new Promise((resolve, reject) => {
      this.setState({ inputErrors: newInputErrors }, () => {
        if (Object.keys(this.state.inputErrors).length) {
          NotificationManager.error(
            'note',
            'Cannot submit the application.',
            'Please check you information and try again.'
          );
          reject();
        } else if (isEmptyArray(this.state.files) && !user.isInternal()) {
          NotificationManager.error(
            'note',
            'Cannot submit the application.',
            'At least one file needs to be uploaded.'
          );
          reject();
        } else {
          resolve();
        }
      });
    });
  };

  submitFamily = () => {
    const { user, updateFamiliesListEntry } = this.props;
    const { name, selectedType, files } = this.state;

    this.validateForm().then(() => {
      const headers = {
        'Content-Type': 'multipart/form-data',
      };

      const requestJSON = {
        name,
        componentTypeId: selectedType.id,
        componentsToAdd: this.componentsIdsToAdd,
      };

      const dataPayload = new FormData();
      dataPayload.append('requestJson', JSON.stringify(requestJSON));
      files.forEach(file => dataPayload.append('reports', file));

      this.modalBody = 'Are you sure you want to submit this application?';
      this.modalConfirmAction = () => {
        this.setState({ requestInProcess: true }, () => {
          http
            .post(componentFamilyAPI.submit, dataPayload, { headers })
            .then(({ data }) => {
              NotificationManager.success(1, 'Your application was submitted successfully');
              this.someDataWasInputted = false;

              updateFamiliesListEntry({}, data);

              this.closeModal();
              user.goToFamilyDetails(data.id);
            })
            .catch(error => {
              this.setState({ requestInProcess: false }, this.closeModal);
            });
        });
      };

      this.openModal();
    });
  };

  openModal = () => {
    this.setState({ isModalOpen: true });
  };

  closeModal = cb => {
    this.setState({ isModalOpen: false }, () => {
      if (typeof cb === 'function') cb();
    });
  };

  blockUnconfirmedTransition = location => {
    const { user } = this.props;

    if (this.someDataWasInputted) {
      this.modalHeader = 'Confirmation';
      this.modalBody = 'You have unsaved data. Are you sure you want to exit application without saving?';
      this.modalConfirmAction = () => {
        this.someDataWasInputted = false;
        this.closeModal(() => {
          user.goTo(location);
        });
      };
      this.openModal();
      return false;
    }
    return true;
  };

  render() {
    const { user, componentsRequestInProcess } = this.props;
    const {
      categories,
      selectedCategory,
      selectedType,
      files,
      inputErrors,
      name,
      requestInProcess,
      isModalOpen,
      familyComponents,
      availableComponentsList,
    } = this.state;

    return (
      <section className='scene__scene-wrapper'>
        <section className='scene__content-wrapper'>
          <section className='scene__scene-header'>
            <header>New Component Family</header>
          </section>
          <section className='scene_section scene_section_two-columns_uneven'>
            <header>General information</header>
            <Input
              mainWrapperClassName='_width_pc_100'
              styleClass={inputStyleClasses.primary}
              label='Component Family Name *'
              maxLength={250}
              alwaysTextField
              error={inputErrors.name}
              value={name}
              onChange={this.handleInputChange('name')}
            />
            <Select
              selectionList={categories || []}
              type={selectType.primary}
              defaultItem={selectedCategory}
              label='Component Category *'
              itemNameIndicator={'name'}
              unclearable
              isLoading={!categories}
              error={inputErrors.selectedCategory}
              handleSelect={this.handleCategorySelect}
            />
            <Select
              selectionList={selectedCategory ? selectedCategory.componentTypes : []}
              type={selectType.primary}
              defaultItem={selectedType}
              label='Component Type *'
              itemNameIndicator='componentTypeName'
              unclearable
              disabled={!selectedCategory}
              error={inputErrors.selectedType}
              handleSelect={this.handleTypeSelect}
            />
          </section>
          {selectedType && (
            <React.Fragment>
              <ComponentList
                familyComponents={familyComponents}
                user={user}
                components={availableComponentsList || []}
                selectComponentForAdd={this.selectComponentForAdd}
                togleAllComponentsForAdd={this.togleAllComponentsForAdd}
                addComponents={this.addComponents}
                selectComponentForDeletion={this.selectComponentForDeletion}
                componentsRequestInProcess={componentsRequestInProcess}
                deleteComponents={this.deleteComponents}
                deleteAllComponents={this.deleteAllComponents}
                deleteSingleComponent={this.deleteSingleComponent}
              />
              <section className='scene_section'>
                <FilePicker
                  files={files}
                  editableStateSectionHeader={`Upload Test Report ${user.isInternal() ? '' : '*'}`}
                  sectionHeaderClassName='scene_section-header'
                  handleFileSelect={this.handleFileSelect}
                  error={inputErrors.files ? '  Upload at least one file' : ''}
                  handleRemoveFile={this.handleRemoveFile}
                />
              </section>
            </React.Fragment>
          )}
          <ApplicationActions
            cancelButtonLabel={'Cancel'}
            saveButtonLabel={'Submit'}
            saveButtonAction={this.submitFamily}
            cancelButtonAction={() => {
              user.goToList(false);
            }}
          />
        </section>
        <Modal isOpen={isModalOpen}>
          {this.modalHeader && <header className='ect-modal-header'>{this.modalHeader}</header>}
          <div className='ect-modal-body'>
            {typeof this.modalBody === 'function' ? this.modalBody() : this.modalBody}
          </div>
          <footer className='ect-modal-footer'>
            <Button
              label='No'
              type={buttonType.secondary}
              color={buttonColor.gray}
              buttonClassName='_height_40 _width_160'
              onClick={this.closeModal}
            />
            <Button
              label='Yes'
              disabledWithSpinner={requestInProcess}
              buttonClassName='_height_40 _width_160'
              onClick={this.modalConfirmAction}
            />
          </footer>
        </Modal>
        <Prompt message={this.blockUnconfirmedTransition} />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/ComponentManufacturer/ComponentFamily/New/index.jsx